import React, { useState, useEffect, useCallback, useRef } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Avatar from "@material-ui/core/Avatar";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import Typography from "@material-ui/core/Typography";
import Checkbox from "@material-ui/core/Checkbox";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SendIcon from "@material-ui/icons/Send";
import { useTheme } from "@material-ui/core/styles";
import { listContacts } from "../../../services/contacts.api";
import { getInitials } from "../../../helpers/getInitials";
import { generateColor } from "../../../helpers/colorGenerator";

const PAGE_SIZE = 30;

const ContactPickerModal = ({ open, onClose, onSend }) => {
  const theme = useTheme();
  const isDark = theme.mode === "dark";

  const [search, setSearch] = useState("");
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [confirming, setConfirming] = useState(false);
  const listRef = useRef(null);
  const searchTimer = useRef(null);

  const bg = isDark ? "#1f2937" : "#ffffff";
  const bgSub = isDark ? "#111827" : "#f0f2f5";
  const textPrimary = isDark ? "#f9fafb" : "#111827";
  const textSecondary = isDark ? "#9ca3af" : "#6b7280";
  const border = isDark ? "#374151" : "#e5e7eb";

  const fetchContacts = useCallback(async (q, p) => {
    setLoading(true);
    try {
      const { data } = await listContacts({ searchParam: q, pageNumber: p, limit: PAGE_SIZE });
      if (p === 1) {
        setContacts(data.contacts || []);
      } else {
        setContacts((prev) => [...prev, ...(data.contacts || [])]);
      }
      setHasMore(data.hasMore);
    } catch (_) {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setSelected([]);
    setContacts([]);
    setPage(1);
    setConfirming(false);
    fetchContacts("", 1);
  }, [open, fetchContacts]);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      fetchContacts(search, 1);
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [search, fetchContacts]);

  const handleScroll = () => {
    const el = listRef.current;
    if (!el || loading || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchContacts(search, nextPage);
    }
  };

  const toggleSelect = (contact) => {
    setSelected((prev) =>
      prev.some((c) => c.id === contact.id)
        ? prev.filter((c) => c.id !== contact.id)
        : [...prev, contact]
    );
  };

  const isChecked = (contact) => selected.some((c) => c.id === contact.id);

  const handleSend = () => {
    if (selected.length === 0) return;
    onSend(selected.map((c) => ({ name: c.name, number: c.number })));
    onClose();
  };

  const selectedNames = selected.map((c) => c.name).join(", ");

  // ── CONFIRMATION VIEW ──────────────────────────────────────────────────────
  if (confirming) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: { borderRadius: 12, backgroundColor: bg, maxHeight: "80vh" },
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "12px 16px",
            borderBottom: `1px solid ${border}`,
            gap: 8,
          }}
        >
          <IconButton size="small" onClick={() => setConfirming(false)}>
            <ArrowBackIcon style={{ color: textPrimary }} />
          </IconButton>
          <Typography style={{ fontWeight: 600, color: textPrimary, fontSize: 16, flex: 1 }}>
            Enviar {selected.length} contacto{selected.length !== 1 ? "s" : ""} a...
          </Typography>
        </div>
        <DialogContent style={{ padding: "8px 0", overflowY: "auto" }}>
          {selected.map((c) => (
            <div
              key={c.id}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "10px 16px",
                gap: 12,
              }}
            >
              <Avatar
                src={c.profilePicUrl}
                style={{
                  width: 46,
                  height: 46,
                  backgroundColor: generateColor(c.number || c.name || ""),
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: 16,
                }}
              >
                {getInitials(c.name || "")}
              </Avatar>
              <div>
                <Typography style={{ fontWeight: 600, color: textPrimary, fontSize: 14 }}>
                  {c.name}
                </Typography>
                <Typography style={{ color: textSecondary, fontSize: 12 }}>
                  {c.number}
                </Typography>
              </div>
            </div>
          ))}
        </DialogContent>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "12px 16px",
            borderTop: `1px solid ${border}`,
          }}
        >
          <IconButton
            onClick={handleSend}
            style={{
              backgroundColor: "#25d366",
              color: "#fff",
              width: 52,
              height: 52,
              boxShadow: "0 2px 12px rgba(37,211,102,0.4)",
            }}
          >
            <SendIcon />
          </IconButton>
        </div>
      </Dialog>
    );
  }

  // ── PICKER VIEW ─────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: { borderRadius: 12, backgroundColor: bg, maxHeight: "85vh", display: "flex", flexDirection: "column" },
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "12px 16px 8px 16px",
          gap: 8,
          flexShrink: 0,
        }}
      >
        <Typography style={{ fontWeight: 600, color: textPrimary, fontSize: 16, flex: 1 }}>
          Enviar contacto
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon style={{ color: textSecondary }} />
        </IconButton>
      </div>

      {/* SEARCH */}
      <div style={{ padding: "4px 16px 8px", flexShrink: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            backgroundColor: bgSub,
            borderRadius: 24,
            padding: "6px 12px",
            gap: 8,
          }}
        >
          <SearchIcon style={{ color: textSecondary, fontSize: 20 }} />
          <InputBase
            autoFocus
            fullWidth
            placeholder="Buscar nombre o número"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ fontSize: 14, color: textPrimary }}
            inputProps={{ style: { color: textPrimary } }}
          />
        </div>
      </div>

      <Divider style={{ backgroundColor: border }} />

      {/* LIST */}
      <div
        ref={listRef}
        onScroll={handleScroll}
        style={{ flex: 1, overflowY: "auto", minHeight: 0 }}
      >
        {contacts.map((c) => (
          <div
            key={c.id}
            onClick={() => toggleSelect(c)}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "8px 16px",
              cursor: "pointer",
              backgroundColor: isChecked(c)
                ? isDark ? "rgba(37,211,102,0.10)" : "rgba(37,211,102,0.07)"
                : "transparent",
              transition: "background-color 0.12s",
              gap: 12,
            }}
          >
            <Checkbox
              checked={isChecked(c)}
              style={{ padding: 0, color: isChecked(c) ? "#25d366" : (isDark ? "#6b7280" : "#9ca3af") }}
              size="small"
            />
            <Avatar
              src={c.profilePicUrl}
              style={{
                width: 44,
                height: 44,
                backgroundColor: generateColor(c.number || c.name || ""),
                color: "#fff",
                fontWeight: "bold",
                fontSize: 15,
              }}
            >
              {getInitials(c.name || "")}
            </Avatar>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography
                style={{
                  fontWeight: 500,
                  color: textPrimary,
                  fontSize: 14,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {c.name}
              </Typography>
              <Typography
                style={{
                  color: textSecondary,
                  fontSize: 12,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {c.number}
              </Typography>
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: 16 }}>
            <CircularProgress size={24} style={{ color: "#25d366" }} />
          </div>
        )}

        {!loading && contacts.length === 0 && (
          <Typography
            style={{
              textAlign: "center",
              color: textSecondary,
              padding: "32px 16px",
              fontSize: 14,
            }}
          >
            No se encontraron contactos
          </Typography>
        )}
      </div>

      {/* BOTTOM BAR — nombres seleccionados + botón enviar */}
      {selected.length > 0 && (
        <>
          <Divider style={{ backgroundColor: border }} />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "10px 16px",
              gap: 12,
              flexShrink: 0,
              backgroundColor: bg,
            }}
          >
            <Typography
              style={{
                flex: 1,
                fontSize: 13,
                color: textSecondary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectedNames}
            </Typography>
            <IconButton
              onClick={() => setConfirming(true)}
              style={{
                backgroundColor: "#25d366",
                color: "#fff",
                width: 46,
                height: 46,
                boxShadow: "0 2px 10px rgba(37,211,102,0.4)",
                flexShrink: 0,
              }}
            >
              <SendIcon />
            </IconButton>
          </div>
        </>
      )}
    </Dialog>
  );
};

export default ContactPickerModal;
